/**
 * H14 — Search drawer live data
 * Preview results (count + 8 cards) via POST /api/v1/search and Suggestions
 * via /shopping-list/autocomplete. Debounced per keystroke, stale responses
 * dropped, in-flight search aborted on change.
 */
import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue'
import { autocomplete } from '@/features/search/api/search'
import { fetchSearchV1 } from '@/features/search/api/searchV1'
import {
  buildFairHomeV2SearchPreviewRequest,
  FAIR_HOME_V2_SEARCH_LIVE_DEBOUNCE_MS,
  FAIR_HOME_V2_SEARCH_SUGGESTIONS_LIMIT,
  mapFairHomeV2SearchPreviewCards,
  mapFairHomeV2SearchSuggestions,
  resolveFairHomeV2SearchPreviewTotal,
  type FairHomeV2SearchPreviewCard,
  type FairHomeV2SearchSuggestion,
} from './fair-home-v2-search-drawer.data'

export function useFairHomeV2SearchLive(input: {
  query: Ref<string>
  imageId: Ref<string | null>
  audience: Ref<string>
  enabled: Ref<boolean>
}) {
  const cards = ref<FairHomeV2SearchPreviewCard[]>([])
  /** `null` while nothing has been fetched for the current input. */
  const total = ref<number | null>(null)
  const loading = ref(false)
  const suggestions = ref<FairHomeV2SearchSuggestion[]>([])

  let previewTimer: ReturnType<typeof setTimeout> | null = null
  let suggestTimer: ReturnType<typeof setTimeout> | null = null
  let previewController: AbortController | null = null
  let previewToken = 0
  let suggestToken = 0

  function clearPreview() {
    if (previewTimer) clearTimeout(previewTimer)
    previewTimer = null
    previewController?.abort()
    previewController = null
    previewToken += 1
    cards.value = []
    total.value = null
    loading.value = false
  }

  function clearSuggestions() {
    if (suggestTimer) clearTimeout(suggestTimer)
    suggestTimer = null
    suggestToken += 1
    suggestions.value = []
  }

  async function runPreview() {
    const request = buildFairHomeV2SearchPreviewRequest({
      query: input.query.value,
      imageId: input.imageId.value,
      audience: input.audience.value,
    })
    if (!request) {
      clearPreview()
      return
    }
    previewController?.abort()
    const controller = new AbortController()
    previewController = controller
    const token = ++previewToken
    loading.value = true
    try {
      const response = await fetchSearchV1(request, { signal: controller.signal })
      if (token !== previewToken) return
      cards.value = mapFairHomeV2SearchPreviewCards(response)
      total.value = resolveFairHomeV2SearchPreviewTotal(response)
    } catch (error) {
      if (token !== previewToken) return
      if ((error as { name?: string })?.name === 'CanceledError') return
      if ((error as { name?: string })?.name === 'AbortError') return
      // Network / 5xx — treat as "no preview" rather than a false empty state.
      cards.value = []
      total.value = null
    } finally {
      if (token === previewToken) loading.value = false
    }
  }

  async function runSuggestions() {
    const text = input.query.value.trim()
    if (!text) {
      clearSuggestions()
      return
    }
    const token = ++suggestToken
    const { data } = await autocomplete({
      search_text: text,
      suggestions_size: FAIR_HOME_V2_SEARCH_SUGGESTIONS_LIMIT,
      suggested_brands_size: 0,
    })
    if (token !== suggestToken) return
    suggestions.value = mapFairHomeV2SearchSuggestions(data?.suggestions, text)
  }

  function schedulePreview() {
    if (previewTimer) clearTimeout(previewTimer)
    previewTimer = setTimeout(() => {
      previewTimer = null
      void runPreview()
    }, FAIR_HOME_V2_SEARCH_LIVE_DEBOUNCE_MS)
  }

  function scheduleSuggestions() {
    if (suggestTimer) clearTimeout(suggestTimer)
    suggestTimer = setTimeout(() => {
      suggestTimer = null
      void runSuggestions()
    }, FAIR_HOME_V2_SEARCH_LIVE_DEBOUNCE_MS)
  }

  const hasInput = computed(
    () => Boolean(input.query.value.trim()) || Boolean(input.imageId.value),
  )

  watch(
    () => [input.query.value.trim(), input.imageId.value, input.audience.value, input.enabled.value] as const,
    ([text, imageId, , enabled]) => {
      if (!enabled || (!text && !imageId)) {
        clearPreview()
      } else {
        // Keep previous cards while the new request is pending (no flash), but
        // reset total so State 5 waits for a real answer.
        total.value = null
        loading.value = true
        schedulePreview()
      }
      if (!enabled || !text) {
        clearSuggestions()
      } else {
        scheduleSuggestions()
      }
    },
  )

  onBeforeUnmount(() => {
    clearPreview()
    clearSuggestions()
  })

  return { cards, total, loading, suggestions, hasInput, reset: () => { clearPreview(); clearSuggestions() } }
}
